

import { Modal, Spinner, Button, Form, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCloseDocumentUploadModal, setOpenDocumentUploadModal } from "../../redux/features/invoice/invoiceSlice";
import { successMessage } from "../../utlis/common";
import "./style.css";
import * as XLSX from "xlsx";
import { useState, useEffect, useRef } from "react";
import { GetProfileDetails } from "../../redux/features/profile/profileActions";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf";
import { FetchInvoiceDocuments, uploadInvoiceDocuments } from "../../redux/features/invoice/invoiceAction";

GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.js',
  import.meta.url
).toString();

interface OptionalDoc {
  type: string;
  file: File | null;
  name: string;
}

interface FileState {
  file: File | null;
  name: string;
}

interface ExtractedData {
  numBoxes: number;
  grossWeight: number;
  netWeight: number;
  customsCode: string;
}

const InvoiceDocumentUploadModal = () => {
  const dispatch: any = useDispatch();
  const { openDocumentUploadModal, modalDocumentUploadData } = useSelector(
    (state: any) => state.invoiceSlice
  );
  console.log("Invoice number for upload:", modalDocumentUploadData.invoiceNumber);
  const isViewMode = modalDocumentUploadData.isViewMode || false;
  const [documentsData, setDocumentsData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [isFullyUploaded, setIsFullyUploaded] = useState(false);
  const [isPartial, setIsPartial] = useState(false);
  const [noDocuments, setNoDocuments] = useState(false);
  const [shipmentType, setShipmentType] = useState<"truck" | "boat">("truck");
  const [isExported, setIsExported] = useState(false);
  const initialRequiredFiles = {
    invoice: { file: null as File | null, name: "" } as FileState,
    packingList: { file: null as File | null, name: "" } as FileState,
    atr: { file: null as File | null, name: "" } as FileState,
    cmr: { file: null as File | null, name: "" } as FileState,
    billOfLading: { file: null as File | null, name: "" } as FileState,
  };
  const [requiredFiles, setRequiredFiles] = useState<Record<keyof typeof initialRequiredFiles, FileState>>(initialRequiredFiles);
  const [optionalDocs, setOptionalDocs] = useState<OptionalDoc[]>([]);
  const [correspondenceChecked, setCorrespondenceChecked] = useState(true);
  const [extractedData, setExtractedData] = useState<Record<string, ExtractedData>>({});
  const [validationError, setValidationError] = useState("");
  const [profileDataLocal, setProfileDataLocal] = useState<any>(null);
  const [correspondenceStatus, setCorrespondenceStatus] = useState<"matched" | "mismatch" | "unchecked">("unchecked");
  const [modalError, setModalError] = useState("");

  useEffect(() => {
    if (!correspondenceChecked) {
      setCorrespondenceStatus("unchecked");
      setValidationError("");
      return;
    }
    if (validateCorrespondence()) {
      setCorrespondenceStatus("matched");
    } else {
      setCorrespondenceStatus("mismatch");
    }
  }, [correspondenceChecked, requiredFiles, documentsData]);

  // Refs for file inputs
  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const packingInputRef = useRef<HTMLInputElement>(null);
  const atrInputRef = useRef<HTMLInputElement>(null);
  const cmrInputRef = useRef<HTMLInputElement>(null);
  const bolInputRef = useRef<HTMLInputElement>(null);
  const optionalInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const loadDraft = (invoiceNumber: string): boolean => {
    const draftStr = localStorage.getItem(`draft_${invoiceNumber}`);
    if (!draftStr) return false;
    try {
      const draft = JSON.parse(draftStr);
      setRequiredFiles((prev) => ({
        ...prev,
        invoice: { file: null, name: draft.requiredFiles.invoice.name || prev.invoice.name },
        packingList: { file: null, name: draft.requiredFiles.packingList.name || prev.packingList.name },
        atr: { file: null, name: draft.requiredFiles.atr.name || prev.atr.name },
        cmr: { file: null, name: draft.requiredFiles.cmr.name || prev.cmr.name },
        billOfLading: { file: null, name: draft.requiredFiles.billOfLading.name || prev.billOfLading.name },
      }));
      setOptionalDocs(
        draft.optionalDocs.map((doc: { type: string; name: string }) => ({
          type: doc.type,
          file: null,
          name: doc.name,
        }))
      );
      setExtractedData(draft.extractedData || {});
      setShipmentType(draft.shipmentType || "truck");
      setCorrespondenceChecked(draft.correspondenceChecked || false);
      setIsExported(draft.isExported || false);
      return true;
    } catch (e) {
      console.error("Failed to load draft:", e);
      return false;
    }
  };

  useEffect(() => {
    if (openDocumentUploadModal && !profileDataLocal) {
      dispatch(GetProfileDetails())
        .then((res: any) => setProfileDataLocal(res?.payload?.data))
        .catch((err) => console.error("Failed to fetch profile:", err));
    }
  }, [openDocumentUploadModal, dispatch]);

  useEffect(() => {
    if (openDocumentUploadModal && !isViewMode && modalDocumentUploadData.invoiceNumber) {
      loadDraft(modalDocumentUploadData.invoiceNumber);
    }
  }, [openDocumentUploadModal, isViewMode, modalDocumentUploadData.invoiceNumber]);

  // Fetch documents for view mode or populate in edit mode
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (openDocumentUploadModal && modalDocumentUploadData.invoiceNumber && isViewMode && !documentsData) {
        setLoading(true);
        try {
          const result = await dispatch(FetchInvoiceDocuments({ invoice_number: modalDocumentUploadData.invoiceNumber })).unwrap();
          if (isMounted) {
            setDocumentsData(result.data);
            const fetchedTipo = result.data.supplier?.country_of_manufacturer || "tipo-1";
            const fetchedIsTipo1 = fetchedTipo === "tipo-1";
            const fetchedIsTipo2 = fetchedTipo === "tipo-2";
            const fetchedIsTipo3 = fetchedTipo === "tipo-3";
            setProfileDataLocal(result.data.supplier);
            setShipmentType(result.data.shipping_mode || "truck");
            setCorrespondenceChecked(result.data.correspondence_checked || false);
            setIsExported(result.data.is_exported || false); // Set is_exported from fetched data
            const extracted: ExtractedData = {
              numBoxes: result.data.number_of_box || 0,
              grossWeight: result.data.gross_weight || 0,
              netWeight: result.data.net_weight || 0,
              customsCode: result.data.customs_code || "",
            };
            const newExtracted: Record<string, ExtractedData> = {};
            newExtracted.invoice = extracted;
            if (!fetchedIsTipo1) newExtracted.packingList = extracted;
            if (fetchedIsTipo2) newExtracted.atr = extracted;
            const fetchedShowCMR = fetchedIsTipo2 && (result.data.shipping_mode || "truck") === "truck";
            const fetchedShowBOL = (fetchedIsTipo2 && (result.data.shipping_mode || "truck") === "boat") || fetchedIsTipo3;
            if (fetchedShowCMR) newExtracted.cmr = extracted;
            if (fetchedShowBOL) newExtracted.billOfLading = extracted;
            setExtractedData(newExtracted);
            const fileNames: Record<keyof typeof initialRequiredFiles, string> = {
              invoice: result.data.invoice_pdf ? extractFileName(result.data.invoice_pdf) : "",
              packingList: result.data.packing_list ? extractFileName(result.data.packing_list) : "",
              atr: result.data.atr ? extractFileName(result.data.atr) : "",
              cmr: result.data.cmr ? extractFileName(result.data.cmr) : "",
              billOfLading: result.data.bill_of_lading ? extractFileName(result.data.bill_of_lading) : "",
            };
            setRequiredFiles({
              invoice: { file: null, name: fileNames.invoice },
              packingList: { file: null, name: fileNames.packingList },
              atr: { file: null, name: fileNames.atr },
              cmr: { file: null, name: fileNames.cmr },
              billOfLading: { file: null, name: fileNames.billOfLading },
            });
            setOptionalDocs(
              (result.data.other_documents || []).map((path: string) => ({
                type: "other",
                file: null,
                name: extractFileName(path),
              }))
            );
            // Determine upload status
            let requiredFields = ["invoice_pdf"];
            if (!fetchedIsTipo1) requiredFields.push("packing_list");
            if (fetchedIsTipo2) requiredFields.push("atr");        // ← only tipo-2
            if (fetchedShowCMR) requiredFields.push("cmr");
            if (fetchedShowBOL) requiredFields.push("bill_of_lading");
            const allPresent = requiredFields.every((field) => !!result.data[field]);
            const anyPresent = requiredFields.some((field) => !!result.data[field]);
            if (allPresent) {
              setIsFullyUploaded(true);
              setIsPartial(false);
              setHasDraft(false);
              setNoDocuments(false);
            } else if (anyPresent) {
              setIsFullyUploaded(false);
              setIsPartial(true);
              setHasDraft(false);
              setNoDocuments(false);
            } else {
              const loaded = loadDraft(modalDocumentUploadData.invoiceNumber);
              if (loaded) {
                setHasDraft(true);
              } else {
                setNoDocuments(true);
              }
              setIsFullyUploaded(false);
              setIsPartial(false);
            }
          }
        } catch (err: any) {
          console.error("Failed to fetch documents:", err);
          if (isMounted) {
            const loaded = loadDraft(modalDocumentUploadData.invoiceNumber);
            if (loaded) {
              setHasDraft(true);
              setDocumentsData(null);
              setIsFullyUploaded(false);
              setIsPartial(false);
              setNoDocuments(false);
            } else {
              setNoDocuments(true);
              setDocumentsData(null);
              setIsFullyUploaded(false);
              setIsPartial(false);
            }
          }
        } finally {
          if (isMounted) setLoading(false);
        }
      } else if (openDocumentUploadModal && !isViewMode && documentsData) {
        // When switching to edit mode, retain and populate from documentsData
        const fetchedTipo = documentsData.supplier?.country_of_manufacturer || "tipo-1";
        const fetchedIsTipo1 = fetchedTipo === "tipo-1";
        const fetchedIsTipo2 = fetchedTipo === "tipo-2";
        const fetchedIsTipo3 = fetchedTipo === "tipo-3";
        setProfileDataLocal(documentsData.supplier);
        setShipmentType(documentsData.shipping_mode || "truck");
        setCorrespondenceChecked(documentsData.correspondence_checked || false);
        setIsExported(documentsData.is_exported || false); // Set is_exported from documentsData
        const extracted: ExtractedData = {
          numBoxes: documentsData.number_of_box || 0,
          grossWeight: documentsData.gross_weight || 0,
          netWeight: documentsData.net_weight || 0,
          customsCode: documentsData.customs_code || "",
        };
        const newExtracted: Record<string, ExtractedData> = {};
        newExtracted.invoice = extracted;
        if (!fetchedIsTipo1) newExtracted.packingList = extracted;
        if (fetchedIsTipo2) newExtracted.atr = extracted;
        const fetchedShowCMR = fetchedIsTipo2 && (documentsData.shipping_mode || "truck") === "truck";
        const fetchedShowBOL = (fetchedIsTipo2 && (documentsData.shipping_mode || "truck") === "boat") || fetchedIsTipo3;
        if (fetchedShowCMR) newExtracted.cmr = extracted;
        if (fetchedShowBOL) newExtracted.billOfLading = extracted;
        setExtractedData(newExtracted);
        const fileNames: Record<keyof typeof initialRequiredFiles, string> = {
          invoice: documentsData.invoice_pdf ? extractFileName(documentsData.invoice_pdf) : "",
          packingList: documentsData.packing_list ? extractFileName(documentsData.packing_list) : "",
          atr: documentsData.atr ? extractFileName(documentsData.atr) : "",
          cmr: documentsData.cmr ? extractFileName(documentsData.cmr) : "",
          billOfLading: documentsData.bill_of_lading ? extractFileName(documentsData.bill_of_lading) : "",
        };
        setRequiredFiles((prev) => ({
          ...prev,
          invoice: { file: null, name: fileNames.invoice || prev.invoice.name },
          packingList: { file: null, name: fileNames.packingList || prev.packingList.name },
          atr: { file: null, name: fileNames.atr || prev.atr.name },
          cmr: { file: null, name: fileNames.cmr || prev.cmr.name },
          billOfLading: { file: null, name: fileNames.billOfLading || prev.billOfLading.name },
        }));
        setOptionalDocs(
          (documentsData.other_documents || []).map((path: string) => ({
            type: "other",
            file: null,
            name: extractFileName(path),
          }))
        );
        // Determine upload status (similar to view mode)
        let requiredFields = ["invoice_pdf"];
        if (!fetchedIsTipo1) requiredFields.push("packing_list");
        if (fetchedIsTipo2) requiredFields.push("atr");        
        if (fetchedShowCMR) requiredFields.push("cmr");
        if (fetchedShowBOL) requiredFields.push("bill_of_lading");
        const allPresent = requiredFields.every((field) => !!documentsData[field]);
        const anyPresent = requiredFields.some((field) => !!documentsData[field]);
        if (allPresent) {
          setIsFullyUploaded(true);
          setIsPartial(false);
          setHasDraft(false);
          setNoDocuments(false);
        } else if (anyPresent) {
          setIsFullyUploaded(false);
          setIsPartial(true);
          setHasDraft(false);
          setNoDocuments(false);
        } else {
          const loaded = loadDraft(modalDocumentUploadData.invoiceNumber);
          if (loaded) {
            setHasDraft(true);
          } else {
            setNoDocuments(true);
          }
          setIsFullyUploaded(false);
          setIsPartial(false);
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [openDocumentUploadModal, modalDocumentUploadData.invoiceNumber, isViewMode, dispatch, documentsData]);

  const tipo = profileDataLocal?.country_of_manufacturer || "tipo-1";
  const isTipo1 = tipo === "tipo-1";
  const isTipo2 = tipo === "tipo-2";
  const isTipo3 = tipo === "tipo-3";
  const showPacking = !isTipo1;
  const showATR = isTipo2;
  const showCMR = isTipo2 && shipmentType === "truck";
  const showBOL = (isTipo2 && shipmentType === "boat") || isTipo3;

  useEffect(() => {
    if (isTipo3) {
      setShipmentType("boat");
    } else if (modalDocumentUploadData?.shipmentMethod && !isViewMode) {
      setShipmentType(modalDocumentUploadData.shipmentMethod);
    }
  }, [modalDocumentUploadData, isTipo3, isViewMode]);

  const handleClose = () => dispatch(setCloseDocumentUploadModal());

  const switchToUpload = () => {
    dispatch(setOpenDocumentUploadModal({
      invoiceNumber: modalDocumentUploadData.invoiceNumber,
      isViewMode: false,
      shipmentMethod: shipmentType,
      documentsData: documentsData,
    }));
  };

  const extractFileName = (path: string) => path.split("/").pop() || "";

  const extractData = async (file: File): Promise<ExtractedData> => {
    const defaultData: ExtractedData = { numBoxes: 0, grossWeight: 0, netWeight: 0, customsCode: "" };
    try {
      const arrayBuffer = await file.arrayBuffer();
      if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
        const loadingTask = getDocument(arrayBuffer);
        const pdf = await loadingTask.promise;
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          fullText += content.items
            .map((item: any) => ('str' in item ? item.str : ''))
            .join(' ') + '\n';
        }
        const numBoxesMatch = fullText.match(/(?:total)[:\s]*(\d+)\s*pallets?/i);
        const data = {
          numBoxes: parseInt(numBoxesMatch?.[1] || '0'),
          grossWeight: parseFloat(fullText.match(/gross\s+weight[:\s]*([\d.]+)/i)?.[1] || '0'),
          netWeight: parseFloat(fullText.match(/net\s+weight[:\s]*([\d.]+)/i)?.[1] || '0'),
          customsCode: fullText.match(/customs\s+code[:\s]*([A-Z0-9]+)/i)?.[1] || '',
        }
        console.log(data, "extracted")
        return data;
      }
      if (file.name.endsWith('.xlsx')) {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
        if (!json.length) return defaultData;
        const headers = json[0].map((h: any) => (h || '').toString().toLowerCase());
        const findIndex = (keywords: string[]) =>
          headers.findIndex((h) => keywords.some((k) => h.includes(k.toLowerCase())));
        const idxBoxes = findIndex(['boxes', 'number of boxes', 'box no']);
        const idxGross = findIndex(['gross', 'gross weight']);
        const idxNet = findIndex(['net', 'net weight']);
        const idxCustoms = findIndex(['customs', 'customs code']);
        let totalRow: any[] | null = null;
        for (let i = json.length - 1; i >= 1; i--) {
          const row = json[i];
          if (
            Array.isArray(row) &&
            row.some((cell: any) =>
              (cell || '').toString().toLowerCase().match(/total\s*:?/)
            )
          ) {
            totalRow = row;
            break;
          }
        }
        if (!totalRow) {
          totalRow = json[json.length - 2] || [];
        }
        const toNum = (v: any): number => {
          if (typeof v === 'number') return isNaN(v) ? 0 : v;
          const s = String(v).trim();
          const hasComma = s.includes(',');
          const hasDot = s.includes('.');
          let normalized: string;
          if (hasComma && hasDot) {
            // e.g. "1.500,25" or "1,500.25" — last separator is the decimal
            normalized = s.lastIndexOf(',') > s.lastIndexOf('.')
              ? s.replace(/\./g, '').replace(',', '.')  // EU: 1.500,25 → 1500.25
              : s.replace(/,/g, '');                    // US: 1,500.25 → 1500.25
          } else if (hasComma) {
            // only comma: "1,5" → decimal; "1,500" → thousands
            const afterComma = s.split(',').pop() || '';
            normalized = (afterComma.length === 3 && /^\d+$/.test(afterComma))
              ? s.replace(/,/g, '')   // thousands separator
              : s.replace(',', '.');  // decimal separator
          } else {
            normalized = s.replace(/[^0-9.]/g, '');
          }
          const n = parseFloat(normalized);
          return isNaN(n) ? 0 : n;
        };
        const xlsxData = {
          numBoxes:
            idxBoxes >= 0
              ? toNum(totalRow[idxBoxes])
              : toNum(totalRow.find((c) => String(c).match(/\d+/))),
          grossWeight: idxGross >= 0 ? toNum(totalRow[idxGross]) : 0,
          netWeight: idxNet >= 0 ? toNum(totalRow[idxNet]) : 0,
          customsCode:
            idxCustoms >= 0
              ? String(totalRow[idxCustoms] || '').trim()
              : '',
        };
        console.log('Extracted XLSX data:', xlsxData);
        return xlsxData;
      }
      return defaultData;
    } catch (error) {
      console.error('Extraction error:', error);
      return defaultData;
    }
  };

  const handleFileUpload = (key: keyof typeof initialRequiredFiles) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRequiredFiles((prev) => ({ ...prev, [key]: { file, name: file.name } }));
    const data = await extractData(file);
    setExtractedData((prev) => ({ ...prev, [key]: data }));
    setValidationError("");
  };

  const handleOptionalUpload = (index: number) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const newOptional = [...optionalDocs];
    newOptional[index] = { ...newOptional[index], file, name: file.name };
    setOptionalDocs(newOptional);
    const data = await extractData(file);
    setExtractedData((prev) => ({ ...prev, [`optional_${index}`]: data }));
  };

  const addOptionalDoc = () => {
    setOptionalDocs((prev) => [...prev, { type: "", file: null, name: "" }]);
    optionalInputRefs.current = optionalInputRefs.current.concat(null);
  };

  const handleTypeChange = (index: number, type: string) => {
    const newOptional = [...optionalDocs];
    newOptional[index] = { ...newOptional[index], type };
    setOptionalDocs(newOptional);
  };

  const isClose = (a: number, b: number, epsilon = 0.001) => Math.abs(a - b) < epsilon;

  const validateCorrespondence = () => {
    if (tipo === "tipo-1") {
      setValidationError(""); // Clear any previous validation error
      return true;
    }
    const keysToCheck: (keyof typeof initialRequiredFiles)[] = ["invoice"];
    if (showPacking) keysToCheck.push("packingList");
    if (showATR) keysToCheck.push("atr");
    if (showCMR) keysToCheck.push("cmr");
    if (showBOL) keysToCheck.push("billOfLading");
    const allUploaded = keysToCheck.every(key => {
      const info = requiredFiles[key];
      return !!info.file || (documentsData && !!info.name);
    });
    if (!allUploaded) {
      setValidationError("Please upload all required documents to enable correspondence check.");
      return false;
    }
    setValidationError(""); // All uploaded
    return true;
  };

  const displayNames = {
    invoice: "Invoice",
    packingList: "Packing List",
    atr: "ATR",
    cmr: "CMR",
    billOfLading: "Bill of Lading",
  } as const;

  const handleSubmit = async () => {
    if (isViewMode) {
      handleClose();
      return;
    }
    setModalError("");
    if (correspondenceChecked && tipo !== "tipo-1" && !validateCorrespondence()) {
      setModalError("Please upload all required documents for correspondence check.");
      return;
    }
    const missingFiles: string[] = [];
    const keysToCheck: (keyof typeof initialRequiredFiles)[] = ["invoice"];
    // if (tipo !== "tipo-1") {
    //   keysToCheck.push("packingList" as const, "atr" as const);
    //   if (showCMR) keysToCheck.push("cmr" as const);
    //   if (showBOL) keysToCheck.push("billOfLading" as const);
    // }
    if (showPacking) {
      keysToCheck.push("packingList");
    }
    if (showATR) {
      keysToCheck.push("atr");
    }
    if (showCMR) {
      keysToCheck.push("cmr");
    }
    if (showBOL) {
      keysToCheck.push("billOfLading");
    }
    for (const key of keysToCheck) {
      const info = requiredFiles[key];
      if (!info.file) {
        if (documentsData && info.name) {
          // Keep existing
        } else {
          const display = displayNames[key];
          missingFiles.push(info.name ? `Please upload ${display} (${info.name})` : display);
        }
      }
    }
    if (missingFiles.length > 0) {
      setModalError(`Please select required files: ${missingFiles.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(requiredFiles).forEach(([key, info]) => {
        if (info.file) formData.append(key === "billOfLading" ? "bill_of_lading" : key, info.file);
      });
      optionalDocs.forEach((doc) => {
        if (doc.file) formData.append("other", doc.file);
      });
      formData.append("shipmentType", shipmentType?.toLowerCase().trim() || "truck");
      formData.append("correspondenceChecked", String(correspondenceChecked));
      formData.append("invoice_number", modalDocumentUploadData.invoiceNumber || "");
      formData.append("supplier_id", String(profileDataLocal?.id || ""));
      formData.append("country_of_manufacturer", profileDataLocal?.country_of_manufacturer || "");
      formData.append("isExported", String(isExported)); // Add isExported to formData
      const extracted = extractedData.invoice || { numBoxes: 0, grossWeight: 0, netWeight: 0, customsCode: "" };
      formData.append("numBoxes", String(extracted.numBoxes || 0));
      formData.append("grossWeight", String(extracted.grossWeight || 0));
      formData.append("netWeight", String(extracted.netWeight || 0));
      formData.append("customsCode", extracted.customsCode || "");
      const resultAction: any = await dispatch(uploadInvoiceDocuments(formData));
      if (uploadInvoiceDocuments.fulfilled.match(resultAction)) {
        localStorage.removeItem(`draft_${modalDocumentUploadData.invoiceNumber}`);
        successMessage("Documents uploaded successfully!");
        handleClose();
      } else {
        setModalError(resultAction.payload?.responseMessage || "Upload failed");
      }
    } catch (err: any) {
      setModalError(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    const invoiceNumber = modalDocumentUploadData.invoiceNumber;
    if (!invoiceNumber) return;
    const draftData = {
      requiredFiles: {
        invoice: { name: requiredFiles.invoice.name },
        packingList: { name: requiredFiles.packingList.name },
        atr: { name: requiredFiles.atr.name },
        cmr: { name: requiredFiles.cmr.name },
        billOfLading: { name: requiredFiles.billOfLading.name },
      },
      optionalDocs: optionalDocs.map((doc) => ({
        type: doc.type,
        name: doc.name,
      })),
      extractedData,
      shipmentType,
      correspondenceChecked,
      isExported, // Save isExported to draft
    };
    localStorage.setItem(`draft_${invoiceNumber}`, JSON.stringify(draftData));
    console.log("Saving draft:", draftData);
    handleClose();
  };

  const triggerUpload = (ref: React.RefObject<HTMLInputElement>) => ref.current?.click();

  const handleRemoveFile = (key: keyof typeof initialRequiredFiles) => {
    setRequiredFiles(prev => ({ ...prev, [key]: { file: null, name: "" } }));
    setExtractedData(prev => {
      const newData = { ...prev };
      delete newData[key];
      return newData;
    });
  };

  const handleRemoveOptionalFile = (index: number) => {
    const updated = [...optionalDocs];
    updated[index] = { ...updated[index], file: null, name: "" };
    setOptionalDocs(updated);
    setExtractedData(prev => {
      const newData = { ...prev };
      delete newData[`optional_${index}`];
      return newData;
    });
  };

  const renderFileStatus = (fileState: FileState, key: keyof typeof initialRequiredFiles) => {
    const { file, name } = fileState;
    const isExisting = !!documentsData && !!name && !isViewMode;
    if (isViewMode) {
      return name ? <span className="text-truncate">{name}</span> : <span className="text-muted">Not uploaded</span>;
    } else if (file) {
      return (
        <>
          <span className="me-2 text-truncate text-success">{name}</span>
          <button type="button" className="remove-btn" onClick={() => handleRemoveFile(key)}>×</button>
        </>
      );
    } else if (name) {
      if (isExisting) {
        return (
          <>
            <span className="me-2 text-truncate text-info">Existing: {name}</span>
            <button type="button" className="remove-btn" onClick={() => handleRemoveFile(key)}>×</button>
            <span className="ms-2 text-muted small">(upload to replace)</span>
          </>
        );
      } else {
        return (
          <>
            <span className="me-2 text-truncate text-warning">Draft: {name}</span>
            <button type="button" className="remove-btn" onClick={() => handleRemoveFile(key)}>×</button>
            <span className="ms-2 text-danger small">(re-select required)</span>
          </>
        );
      }
    } else {
      return <span className="text-muted">Not uploaded</span>;
    }
  };

  const renderOptionalStatus = (doc: OptionalDoc, index: number) => {
    const { file, name } = { file: doc.file, name: doc.name || doc.file?.name || '' };
    const isExisting = !!documentsData && !!name && !isViewMode;
    if (isViewMode) {
      return name ? <span className="text-truncate">{name}</span> : <span className="text-muted">Not uploaded</span>;
    } else if (file) {
      return (
        <>
          <span className="me-2 text-truncate text-success">{file.name}</span>
          <button type="button" className="remove-btn" onClick={() => handleRemoveOptionalFile(index)}>×</button>
        </>
      );
    } else if (name) {
      if (isExisting) {
        return (
          <>
            <span className="me-2 text-truncate text-info">Existing: {name}</span>
            <button type="button" className="remove-btn" onClick={() => handleRemoveOptionalFile(index)}>×</button>
            <span className="ms-2 text-muted small">(upload to replace)</span>
          </>
        );
      } else {
        return (
          <>
            <span className="me-2 text-truncate text-warning">Draft: {name}</span>
            <button type="button" className="remove-btn" onClick={() => handleRemoveOptionalFile(index)}>×</button>
            <span className="ms-2 text-info small">(optional)</span>
          </>
        );
      }
    } else {
      return <span className="text-muted">Not uploaded</span>;
    }
  };

  if (!openDocumentUploadModal) return null;

  return (
    <Modal show={openDocumentUploadModal} onHide={handleClose} size="xl">
      <Modal.Header className="invoice-header" closeButton>
        <Modal.Title>{isViewMode ? "View Shipment Documentation" : "Upload Shipment Documentation"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-4">
            <Spinner animation="border" role="status" />
            <span className="mt-2">Loading...</span>
          </div>
        ) : (
          <>
            {isViewMode ? (
              <>
                {noDocuments ? (
                  <div className="alert alert-warning mt-3">
                    No documents have been uploaded yet. Please upload them to proceed.
                    <Button
                      variant="primary"
                      size="sm"
                      className="ms-2"
                      onClick={switchToUpload}
                    >
                      Upload Documents
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Required Documents */}
                    <h5 className="section-title">Required Documents</h5>
                    <div className="documents-section">
                      {/* --- Invoice --- */}
                      <div className="doc-block">
                        <Row className="align-items-center">
                          <Col md={3}><strong>Invoice PDF {isViewMode ? "" : "*"}</strong></Col>
                          <Col md={3}>
                            {!isViewMode && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => triggerUpload(invoiceInputRef)}
                                >
                                  Upload
                                </Button>
                                <input
                                  ref={invoiceInputRef}
                                  type="file"
                                  accept=".pdf,.xlsx"
                                  style={{ display: "none" }}
                                  onChange={handleFileUpload("invoice")}
                                />
                              </>
                            )}
                          </Col>
                          <Col md={6} className="d-flex align-items-center">
                            {renderFileStatus(requiredFiles.invoice, "invoice")}
                          </Col>
                        </Row>
                      </div>
                      {/* --- Packing List --- */}
                      {showPacking && (
                        <div className="doc-block">
                          <Row className="align-items-center">
                            <Col md={3}><strong>Packing List {isViewMode ? "" : "*"}</strong></Col>
                            <Col md={3}>
                              {!isViewMode && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => triggerUpload(packingInputRef)}
                                  >
                                    Upload
                                  </Button>
                                  <input
                                    ref={packingInputRef}
                                    type="file"
                                    accept=".pdf,.xlsx"
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload("packingList")}
                                  />
                                </>
                              )}
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                              {renderFileStatus(requiredFiles.packingList, "packingList")}
                            </Col>
                          </Row>
                        </div>
                      )}
                      {/* --- ATR --- */}
                      {showATR && (
                        <div className="doc-block">
                          <Row className="align-items-center">
                            <Col md={3}><strong>ATR {isViewMode ? "" : "*"}</strong></Col>
                            <Col md={3}>
                              {!isViewMode && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => triggerUpload(atrInputRef)}
                                  >
                                    Upload
                                  </Button>
                                  <input
                                    ref={atrInputRef}
                                    type="file"
                                    accept=".pdf"
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload("atr")}
                                  />
                                </>
                              )}
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                              {renderFileStatus(requiredFiles.atr, "atr")}
                            </Col>
                          </Row>
                        </div>
                      )}
                      {/* --- CMR --- */}
                      {showCMR && (
                        <div className="doc-block">
                          <Row className="align-items-center">
                            <Col md={3}><strong>CMR {isViewMode ? "" : "*"}</strong></Col>
                            <Col md={3}>
                              {!isViewMode && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => triggerUpload(cmrInputRef)}
                                  >
                                    Upload
                                  </Button>
                                  <input
                                    ref={cmrInputRef}
                                    type="file"
                                    accept=".pdf"
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload("cmr")}
                                  />
                                </>
                              )}
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                              {renderFileStatus(requiredFiles.cmr, "cmr")}
                            </Col>
                          </Row>
                        </div>
                      )}
                      {/* --- Bill of Lading --- */}
                      {showBOL && (
                        <div className="doc-block">
                          <Row className="align-items-center">
                            <Col md={3}><strong>Bill of Lading {isViewMode ? "" : "*"}</strong></Col>
                            <Col md={3}>
                              {!isViewMode && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => triggerUpload(bolInputRef)}
                                  >
                                    Upload
                                  </Button>
                                  <input
                                    ref={bolInputRef}
                                    type="file"
                                    accept=".pdf"
                                    style={{ display: "none" }}
                                    onChange={handleFileUpload("billOfLading")}
                                  />
                                </>
                              )}
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                              {renderFileStatus(requiredFiles.billOfLading, "billOfLading")}
                            </Col>
                          </Row>
                          {!isViewMode && !requiredFiles.billOfLading.file && (!documentsData || !requiredFiles.billOfLading.name) && (
                            <div className="alert alert-danger mt-2">
                              Bill of Lading is required for boat shipments
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isPartial && (
                      <div className="alert alert-warning mt-3">
                        Some required documents are missing. Please complete the upload.
                      </div>
                    )}
                    {hasDraft && (
                      <div className="alert alert-info mt-3">
                        Some documents are saved as draft.
                        <Button
                          variant="primary"
                          size="sm"
                          className="ms-2"
                          onClick={switchToUpload}
                        >
                          Continue to upload from drafts
                        </Button>
                      </div>
                    )}
                    {/* Optional Documents */}
                    {optionalDocs.length > 0 && (
                      <>
                        <h5 className="section-title">Optional Documents</h5>
                        <div className="optional-section mb-4">
                          {optionalDocs.map((doc, index) => (
                            <Row key={index} className="mb-2">
                              <Col md={3}>
                                <span>{doc.type || "Other"}</span>
                              </Col>
                              <Col md={3}></Col>
                              <Col md={6} className="d-flex align-items-center">
                                {renderOptionalStatus(doc, index)}
                              </Col>
                            </Row>
                          ))}
                        </div>
                      </>
                    )}
                    {/* Export Status */}
                    <div className="d-flex align-items-center mt-2">
                      <strong>Export Status: </strong>
                      {isViewMode ? (
                        <span className="ms-2" style={{
                          color: isExported ? '#198754' : '#dc3545', // green / red
                          fontWeight: '800',
                        }}>
                          {isExported ? 'Exported' : 'Not Exported'} </span>
                      ) : (
                        <Form.Check
                          type="switch"
                          id="is-exported-switch"
                          label={isExported ? "Exported" : "Not Exported"}
                          checked={isExported}
                          onChange={(e) => setIsExported(e.target.checked)}
                          className="ms-2"
                        />
                      )}
                    </div>
                    {!isTipo1 && (
                      <div className="d-flex align-items-center mt-2">
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor:
                              correspondenceStatus === "matched" ? "green" :
                                correspondenceStatus === "mismatch" ? "red" :
                                  "#ccc",
                            color: "white",
                            fontWeight: "bold",
                            marginRight: "8px",
                          }}
                        >
                          {correspondenceStatus === "matched" ? "✓" :
                            correspondenceStatus === "mismatch" ? "✕" : "-"}
                        </span>
                        <span>
                          Correspondence between Packing List, Invoice, and CMR/BOL<br />
                          <small className="text-muted">(Number of boxes, Gross weight, Net weight, Customs code)</small>
                        </span>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {/* Required Documents */}
                <h5 className="section-title">Required Documents</h5>
                <div className="documents-section">
                  {/* --- Invoice --- */}
                  <div className="doc-block">
                    <Row className="align-items-center">
                      <Col md={3}><strong>Invoice PDF *</strong></Col>
                      <Col md={3}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => triggerUpload(invoiceInputRef)}
                        >
                          Upload
                        </Button>
                        <input
                          ref={invoiceInputRef}
                          type="file"
                          accept=".pdf,.xlsx"
                          style={{ display: "none" }}
                          onChange={handleFileUpload("invoice")}
                        />
                      </Col>
                      <Col md={6} className="d-flex align-items-center">
                        {renderFileStatus(requiredFiles.invoice, "invoice")}
                      </Col>
                    </Row>
                    {!requiredFiles.invoice.file && (!documentsData || !requiredFiles.invoice.name) && (
                      <div className="alert alert-danger mt-2">
                        Invoice PDF is required
                      </div>
                    )}
                  </div>
                  {/* --- Packing List --- */}
                  {showPacking && (
                    <div className="doc-block">
                      <Row className="align-items-center">
                        <Col md={3}><strong>Packing List *</strong></Col>
                        <Col md={3}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => triggerUpload(packingInputRef)}
                          >
                            Upload
                          </Button>
                          <input
                            ref={packingInputRef}
                            type="file"
                            accept=".pdf,.xlsx"
                            style={{ display: "none" }}
                            onChange={handleFileUpload("packingList")}
                          />
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                          {renderFileStatus(requiredFiles.packingList, "packingList")}
                        </Col>
                      </Row>
                      {!requiredFiles.packingList.file && (!documentsData || !requiredFiles.packingList.name) && (
                        <div className="alert alert-danger mt-2">
                          Packing List is required
                        </div>
                      )}
                    </div>
                  )}
                  {/* --- ATR --- */}
                  {showATR && (
                    <div className="doc-block">
                      <Row className="align-items-center">
                        <Col md={3}><strong>ATR *</strong></Col>
                        <Col md={3}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => triggerUpload(atrInputRef)}
                          >
                            Upload
                          </Button>
                          <input
                            ref={atrInputRef}
                            type="file"
                            accept=".pdf"
                            style={{ display: "none" }}
                            onChange={handleFileUpload("atr")}
                          />
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                          {renderFileStatus(requiredFiles.atr, "atr")}
                        </Col>
                      </Row>
                      {!requiredFiles.atr.file && (!documentsData || !requiredFiles.atr.name) && (
                        <div className="alert alert-danger mt-2">
                          ATR is required
                        </div>
                      )}
                    </div>
                  )}
                  {/* --- CMR --- */}
                  {showCMR && (
                    <div className="doc-block">
                      <Row className="align-items-center">
                        <Col md={3}><strong>CMR *</strong></Col>
                        <Col md={3}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => triggerUpload(cmrInputRef)}
                          >
                            Upload
                          </Button>
                          <input
                            ref={cmrInputRef}
                            type="file"
                            accept=".pdf"
                            style={{ display: "none" }}
                            onChange={handleFileUpload("cmr")}
                          />
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                          {renderFileStatus(requiredFiles.cmr, "cmr")}
                        </Col>
                      </Row>
                      {!requiredFiles.cmr.file && (!documentsData || !requiredFiles.cmr.name) && (
                        <div className="alert alert-danger mt-2">
                          CMR is required
                        </div>
                      )}
                    </div>
                  )}
                  {/* --- Bill of Lading --- */}
                  {showBOL && (
                    <div className="doc-block">
                      <Row className="align-items-center">
                        <Col md={3}><strong>Bill of Lading *</strong></Col>
                        <Col md={3}>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => triggerUpload(bolInputRef)}
                          >
                            Upload
                          </Button>
                          <input
                            ref={bolInputRef}
                            type="file"
                            accept=".pdf"
                            style={{ display: "none" }}
                            onChange={handleFileUpload("billOfLading")}
                          />
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                          {renderFileStatus(requiredFiles.billOfLading, "billOfLading")}
                        </Col>
                      </Row>
                      {!requiredFiles.billOfLading.file && (!documentsData || !requiredFiles.billOfLading.name) && (
                        <div className="alert alert-danger mt-2">
                          Bill of Lading is required for boat shipments
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {modalError && (
                  <div className="alert alert-danger mt-3">
                    {modalError}
                  </div>
                )}
                {/* Optional Documents */}
                <h5 className="section-title">Optional Documents</h5>
                <div className="optional-section mb-4">
                  {optionalDocs.map((doc, index) => (
                    <Row key={index} className="mb-2">
                      <Col md={3}>
                        <Form.Select
                          value={doc.type}
                          onChange={(e) => handleTypeChange(index, e.target.value)}
                        >
                          <option value="">Select document type</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => optionalInputRefs.current[index]?.click()}
                        >
                          Upload
                        </Button>
                        <input
                          ref={(el) => (optionalInputRefs.current[index] = el)}
                          type="file"
                          accept=".pdf,.xlsx"
                          style={{ display: "none" }}
                          onChange={handleOptionalUpload(index)}
                        />
                      </Col>
                      <Col md={6} className="d-flex align-items-center">
                        {renderOptionalStatus(doc, index)}
                      </Col>
                    </Row>
                  ))}
                  <Button variant="link" onClick={addOptionalDoc}>
                    + Add another document
                  </Button>
                </div>
                {/* Export Status */}
                <div className="d-flex align-items-center mt-2">
                  <Form.Check
                    type="switch"
                    id="is-exported-switch"
                    label={isExported ? "Exported" : "Not Exported"}
                    checked={isExported}
                    onChange={(e) => setIsExported(e.target.checked)}
                  />
                </div>
                {!isTipo1 && (
                  <div className="d-flex align-items-center mt-2">
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor:
                          correspondenceStatus === "matched" ? "green" :
                            correspondenceStatus === "mismatch" ? "red" :
                              "#ccc",
                        color: "white",
                        fontWeight: "bold",
                        marginRight: "8px",
                      }}
                    >
                      {correspondenceStatus === "matched" ? "✓" :
                        correspondenceStatus === "mismatch" ? "✕" : "-"}
                    </span>
                    <span>
                      Correspondence between Packing List, Invoice, and CMR/BOL<br />
                      <small className="text-muted">(Number of boxes, Gross weight, Net weight, Customs code)</small>
                    </span>
                  </div>
                )}
                {validationError && (
                  <div className="alert alert-warning mt-3">{validationError}</div>
                )}
                {/* Footer Buttons */}
                <div className="d-flex justify-content-end mt-4">
                  <Button variant="secondary" className="me-2" onClick={handleSaveDraft}>
                    Save Draft
                  </Button>
                  <Button onClick={handleSubmit} disabled={loading}>
                    Submit Documentation
                  </Button>
                </div>
              </>
            )}
            {/* View Mode Footer */}
            {isViewMode && (
              <div className="d-flex justify-content-end mt-4 pt-3 pb-3 bg-light border-top rounded-bottom shadow-sm">
                {(isFullyUploaded || isPartial) && (
                  <Button
                    variant={isPartial ? "warning" : "primary"}
                    className="me-3 px-4"
                    onClick={switchToUpload}
                    size="sm"
                  >
                    {isPartial ? "Complete Upload" : "Edit Documents"}
                  </Button>
                )}
                <Button variant="secondary" className="px-4" onClick={handleClose} size="sm">
                  Close
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InvoiceDocumentUploadModal;

